import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom'; 
import styled from 'styled-components';
import { useChat } from '../context/ChatProvider';
import axios from 'axios';
import { useTranslation } from "react-i18next";
import PasswordField from './PasswordField';
import { GoogleLogin } from '@react-oauth/google';
import { ErrorMessage } from '../styled/ErrorMessage';
import LineLoginButton from "./LineLoginButton";
//import Signup from "./Signup";

const LoginContainer = styled.form`
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    justify-content: center;
    padding: 0.8rem 0;
    width: 100%;
    max-width: 280px;

    & button {
        display: flex;
        padding: 10px 20px;
        border: none;
        border-radius: 12px;
        color: white;
        font-weight: 600;
        background: linear-gradient(135deg, #6366f1, #8b5cf6);
        transition: all 0.2s ease;
        cursor: pointer;
        box-shadow: 0 3px 14px rgba(99, 102, 241, 0.3);

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 5px 20px rgba(99, 102, 241, 0.45);
        }
        &:active { transform: translateY(0); }
    }
`;

const Input = styled.input`
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.08);
    padding: 0.7rem 1rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    font-family: 'Poppins', sans-serif;
    transition: border-color 0.2s, background 0.2s;

    &::placeholder { color: rgba(255, 255, 255, 0.3); }
    &:focus {
        outline: none;
        border-color: rgba(99, 102, 241, 0.5);
        background: rgba(255, 255, 255, 0.08);
    }
`;

// const ErrorHeader = styled.div`
//   color: red;
//   font-size: 0.8rem;
//   width:60%;
// `;

const Button = styled.button`
  width: 100%;
  padding: 0.7rem 0;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 3px 14px rgba(99, 102, 241, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 5px 20px rgba(99, 102, 241, 0.45);
  }
  &:active { transform: translateY(0); }
`;

const LoginSeperator = styled.div`
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.85em;
  margin: 0.5rem 0;
  width: 100%;
  max-width: 280px;
  text-align: center;
`;

const PasswordResetLink = styled(Link)`
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.8em;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover { color: rgba(255, 255, 255, 0.7); }
`;

const SignupContainer = styled.div`
  margin-top: 1.5em;
  font-size: 0.8em;
  color: rgba(255, 255, 255, 0.4);
`;

const SignupLink = styled(Link)`
  color: #a5b4fc;
  font-size: 1em;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover { color: #c7d2fe; }
`;

const SocialButtonWrapper = styled.div`
  width: 100%;
  max-width: 280px;
  display: flex;
  justify-content: center;
  padding: 4px 0;
`;

const StyledGoogleLogin = styled(GoogleLogin)`
  width: 20em; // Custom width
  // Add more custom styles here
`;


const Login = ({initialMessage}) => {
    const inputEmail = useRef(null);
    const inputPassword = useRef(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const { user, setUser, setToken, joinRoom, fetchRooms, setCurrentRoom, resetContextFor } = useChat();
    const baseUrl = process.env.REACT_APP_SERVER;
    const navigate = useNavigate(); 

    const { t } = useTranslation();
    
    const wasGuest = user?.guest;
    
    //console.log(process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID);

    useEffect(() => {
        // load initial message if there is one
        if(initialMessage){
            setErrorMessage(initialMessage);
        }
    },[]);
    
    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsSubmitting(true);

        const email = inputEmail.current.value;
        const password = inputPassword.current.value;

        try {
        
            if(!email || !password) {
                setIsSubmitting(false);
                return;
            }
            
            // check if logging in from being guest, as we will need to reload the chat
            
            
            const response = await axios.post(`${baseUrl}/login`, { email: email, password: password }, { withCredentials: true });

            const { token, user } = response.data;

            if (token && user) {
                
                //console.log("token + authUser");
                
                setToken(token);
                resetContextFor(user);
                
                sessionStorage.setItem('user', JSON.stringify(user));
                sessionStorage.setItem('token', JSON.stringify(token));
                sessionStorage.setItem('userLoggedInWithGoogle', "FALSE");
                setErrorMessage(null);

                // NOTE : socket.io connection is made in ChatProvider once User and Token is set
                
                if(wasGuest){
                    
                    // TODO associate worm chat to user logging in, delete guest account
                    
                    sessionStorage.setItem('currentRoom', JSON.stringify(user.latestRoom));
                    setCurrentRoom(user.latestRoom);
                    await fetchRooms();
                    
                    //navigate('/init-chat');
                    window.location.href = window.location.origin + '/init-chat';
                }
                
                navigate('/chat');

            }else{
               // console.log('no token or authUser returned');
                // something went wrong, display the returned message to the user
                if(response.data.message){
                    setErrorMessage(response.data.message);
                }else{
                    setErrorMessage(t('LoginError'));
                }
            }

        } catch (err) {
            console.log(err);
            setErrorMessage(t('LoginError'));
        }finally{
            setIsSubmitting(false);
        }
    }

    // Google OAuth handling
    const googleSuccess = async (response) => {
        try {
            const idToken = response.credential; // Extract the ID token from Google response

            //console.log(response);
            //console.log('Google success! token=', idToken);
            
            // Send the ID token to your backend for verification
            const backendResponse = await axios.post(`${process.env.REACT_APP_SERVER}/v/gtoken`,
                { idToken: idToken },
                { withCredentials: true }
            );
            
            const { token, user } = backendResponse.data;

            if (token && user) {
                setToken(token);
                setUser(user);
                sessionStorage.setItem('user', JSON.stringify(user));
                sessionStorage.setItem('token', JSON.stringify(token));
                sessionStorage.setItem('userLoggedInWithGoogle', "TRUE");
                setErrorMessage(null);
                // NOTE : socket.io connection is made in ChatProvider once User and Token is set
                
                // Check for last room and join
                // if(user.latestRoom){
                //     joinRoom(user.latestRoom.id);
                // }
                
                
                navigate('/chat');
            } else {
                // Handle the case where the ID token is not verified
                console.error("Invalid Google ID token / Unable to authenticate");
                setErrorMessage("Failed to login with Google.")
            }
        } catch (error) {
            //console.error("Error handling Google login", error);
            setErrorMessage("Failed to login with Google.")
        }
    };

    const googleError = (response) => {
        //console.error("Google Login Error", response);
        setErrorMessage("Failed to login with Google.")
    };

    return (
        <>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <LoginContainer onSubmit={ handleSubmit }>
                <Input type="text" inputMode="email" placeholder={t('HomepageEmail')} ref={ inputEmail } maxLength={70} />
                <PasswordField placeholder={t('HomepagePassword')} reference={inputPassword} maxLength={50} />
                <Button type="submit" disabled={isSubmitting}>
                    {t('LoginButton')}
                </Button>
            </LoginContainer>
            <LoginSeperator>- OR -</LoginSeperator>
            <SocialButtonWrapper>
                <LineLoginButton text={t('LineSignIn')} />
            </SocialButtonWrapper>
            <SocialButtonWrapper>
                <GoogleLogin
                    clientId={process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID}
                    buttonText={t('GoogleSignin')}
                    width="280"
                    onSuccess={googleSuccess}
                    onError={googleError}
                    cookiePolicy={'single_host_origin'}
                />
            </SocialButtonWrapper>
           
            <PasswordResetLink to="/forgot-password">{t('ForgotPasswordLink')}</PasswordResetLink>
            
            <SignupContainer>
                {t('DontHaveAccount')} <SignupLink to="/signup">{t('AccountSignUp')}</SignupLink>
            </SignupContainer>
            
        </>
    );
};

export default Login;